import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, HandHeart, ShieldCheck, Phone } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/site";
import Seo from "@/components/site/Seo";

const PILLARS = [
  {
    I: Heart,
    title: "Compassion",
    body:
      "We listen first. Every client is met with warmth, patience, and genuine interest in the life they’ve built.",
  },
  {
    I: HandHeart,
    title: "Dignity",
    body:
      "Care is delivered with respect — honoring independence, preferences, and the quiet moments that matter most.",
  },
  {
    I: ShieldCheck,
    title: "Reliability",
    body:
      "Consistent caregivers, predictable schedules, and a team you can reach any time of day or night.",
  },
];

const ABOUT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Elite Home Care by Hortense",
  "url": "https://yourelitehomecare.com/about"
};

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        path="/about"
        title="About Hortense — Founder of Elite Home Care"
        description="Elite Home Care was founded by Hortense on a simple belief: every senior deserves to age with dignity in the home they love. Meet the family-first team caring for SWFL seniors like their own."
        jsonLd={ABOUT_JSONLD}
      />
      {/* Hero */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div>
            <span className="overline">About Hortense</span>
            <h1 className="text-5xl md:text-6xl leading-[1.05]">
              A daughter’s love,{" "}
              <span className="italic text-gold-500">
                turned into a calling.
              </span>
            </h1>
            <p className="mt-6 text-navy-700 text-lg leading-relaxed">
              Elite Home Care began the way many important things do — in the
              quiet of a family kitchen. When Hortense stepped in to care for
              her own mother, she saw how much peace one dependable, loving
              presence could bring.
            </p>
            <p className="mt-4 text-navy-600 leading-relaxed">
              Today, that same presence defines every caregiver we hire, every
              plan we design, and every family we have the privilege to serve.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="about-cta-consultation"
                className="btn-primary"
              >
                Schedule a Free Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={BRAND.phoneHref}
                data-testid="about-cta-call"
                className="btn-secondary"
              >
                <Phone className="h-4 w-4" strokeWidth={1.75} /> {BRAND.phone}
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-5 bg-gold-100/60 rounded-3xl -z-10" />
            <img
              src={IMAGES.founder}
              alt="Hortense, founder of Elite Home Care"
              className="w-full rounded-2xl object-cover shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="section bg-navy-50/60 border-y border-navy-900/5">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">Our values</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              The three pillars that guide every visit.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map(({ I, title, body }) => (
              <div
                key={title}
                data-testid={`about-pillar-${title.toLowerCase()}`}
                className="card-soft p-8 md:p-10"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy-900 text-gold-300">
                  <I className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-navy-900">
                  {title}
                </h3>
                <p className="mt-3 text-navy-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="section">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-navy-900 text-white p-10 md:p-16 text-center">
            <div
              aria-hidden
              className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gold-400/20 blur-3xl"
            />
            <span className="text-gold-300 text-xs uppercase tracking-[0.28em] font-medium">
              Our promise
            </span>
            <blockquote className="mt-5 font-serif text-3xl md:text-5xl leading-snug max-w-4xl mx-auto">
              “We treat your loved ones{" "}
              <em className="text-gold-300 not-italic italic">like our own.</em>
              <br />
              That promise guides everything we do.”
            </blockquote>
            <p className="mt-6 text-white/70">— Hortense, Founder</p>
            <Link
              to="/contact"
              data-testid="about-promise-cta"
              className="btn-primary mt-10"
            >
              Start the conversation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
